import {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {ContentHotel} from '@findhotel/sapi'
import {uniq} from 'ramda'

export interface ImageCategory {
  id: string
  name: string
  count: number
  images: string[]
  default?: boolean
}

export const DEFAULT_CATEGORY_ID = 'all'

export const useHotelImagesCategories = (
  imagesWithCategories: ContentHotel['images'] = []
) => {
  const activeCategoryPillRef = useRef<HTMLButtonElement>(null)
  const {formatMessage} = useIntl()
  const allPhotosMessage = formatMessage({
    id: 'allPhotos',
    defaultMessage: 'All photos'
  })
  const [activeCategoryId, setActiveCategoryId] =
    useState<string>(DEFAULT_CATEGORY_ID)

  const categories = useMemo(() => {
    const groupedCategories: Record<string, ImageCategory> = {}

    const allPhotosCategory: ImageCategory = {
      id: DEFAULT_CATEGORY_ID,
      name: allPhotosMessage,
      default: true,
      count: 0,
      images: []
    }

    for (const image of imagesWithCategories) {
      if (!image?.href) continue

      const imageCategories = uniq(image.category)

      for (const category of imageCategories) {
        const categoryId = category?.id

        if (!categoryId) continue

        if (!groupedCategories[categoryId]) {
          groupedCategories[categoryId] = {
            id: categoryId,
            name: category?.name,
            count: 0,
            images: []
          }
        }

        groupedCategories[categoryId].images.push(image.href)
        groupedCategories[categoryId].count++
      }

      allPhotosCategory.images.push(image.href)
      allPhotosCategory.count++
    }

    if (!allPhotosCategory.count) return []

    const sortedCategories = Object.values(groupedCategories).sort(
      (a, b) => b.count - a.count
    )

    return [allPhotosCategory, ...sortedCategories]
  }, [imagesWithCategories, allPhotosMessage])

  const activeCategory: ImageCategory = useMemo(() => {
    if (!categories.length) {
      return {} as ImageCategory
    }

    const category = categories.find(
      category => category?.id === activeCategoryId
    )

    if (!category?.id) return categories[0]
    return category
  }, [activeCategoryId, categories])

  const totalImages = useMemo(() => {
    const defaultCategory = categories.find(category => category.default)
    return defaultCategory?.count ?? 0
  }, [categories])

  useEffect(() => {
    if (activeCategory) {
      activeCategoryPillRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }
  }, [activeCategory])

  return {
    totalImages,
    activeCategory,
    categories,
    activeCategoryPillRef,
    setActiveCategoryId
  }
}
